import { z } from 'zod';
import { JurisdictionLevelSchema } from '../common/enums.js';
import { MetadataSchema, PaginationQuerySchema } from '../common/types.js';
export const TaxTypeSchema = z.enum(['percentage', 'fixed', 'compound']);
export const AppliesToSchema = z.enum([
    'all',
    'base_plan',
    'addons',
    'usage',
    'line_items',
]);
export const TaxableAmountTypeSchema = z.enum(['net', 'gross']);
export const TaxSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    name: z.string(),
    code: z.string(),
    description: z.string().nullable(),
    tax_type: TaxTypeSchema,
    percentage_value: z.number().nullable(),
    fixed_amount_cents: z.number().nullable(),
    currency: z.string().nullable(),
    applies_to: AppliesToSchema.nullable(),
    taxable_amount_type: TaxableAmountTypeSchema.nullable(),
    compound: z.boolean(),
    jurisdiction: z.string().nullable(),
    jurisdiction_level: JurisdictionLevelSchema.nullable(),
    tax_authority: z.string().nullable(),
    external_tax_service: z.string().nullable(),
    external_tax_id: z.string().nullable(),
    effective_from: z.string().date().nullable(),
    effective_to: z.string().date().nullable(),
    auto_apply: z.boolean(),
    is_active: z.boolean(),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateTaxSchema = z
    .object({
    name: z.string().min(1).max(255),
    code: z.string().min(1).max(100),
    description: z.string().optional(),
    tax_type: TaxTypeSchema,
    percentage_value: z.number().min(0).max(100).optional(),
    fixed_amount_cents: z.number().min(0).optional(),
    currency: z.string().optional(),
    applies_to: AppliesToSchema.optional(),
    taxable_amount_type: TaxableAmountTypeSchema.optional(),
    compound: z.boolean().default(false),
    jurisdiction: z.string().optional(),
    jurisdiction_level: JurisdictionLevelSchema.optional(),
    tax_authority: z.string().optional(),
    external_tax_service: z.string().optional(),
    external_tax_id: z.string().optional(),
    effective_from: z.string().date().optional(),
    effective_to: z.string().date().optional(),
    auto_apply: z.boolean().default(false),
    is_active: z.boolean().default(true),
    metadata: MetadataSchema.default({}),
})
    .refine(data => {
    if (data.tax_type === 'percentage' || data.tax_type === 'compound') {
        return data.percentage_value !== undefined;
    }
    if (data.tax_type === 'fixed') {
        return data.fixed_amount_cents !== undefined;
    }
    return true;
}, {
    message: 'percentage_value required for percentage/compound, fixed_amount_cents required for fixed',
});
export const UpdateTaxSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    code: z.string().min(1).max(100).optional(),
    description: z.string().optional(),
    tax_type: TaxTypeSchema.optional(),
    percentage_value: z.number().min(0).max(100).optional(),
    fixed_amount_cents: z.number().min(0).optional(),
    currency: z.string().optional(),
    applies_to: AppliesToSchema.optional(),
    taxable_amount_type: TaxableAmountTypeSchema.optional(),
    compound: z.boolean().optional(),
    jurisdiction: z.string().optional(),
    jurisdiction_level: JurisdictionLevelSchema.optional(),
    tax_authority: z.string().optional(),
    external_tax_service: z.string().optional(),
    external_tax_id: z.string().optional(),
    effective_from: z.string().date().optional(),
    effective_to: z.string().date().optional(),
    auto_apply: z.boolean().optional(),
    is_active: z.boolean().optional(),
    metadata: MetadataSchema.optional(),
});
export const TaxQuerySchema = PaginationQuerySchema.extend({
    tax_type: z.union([TaxTypeSchema, z.literal('all')]).optional(),
    jurisdiction: z.string().optional(),
    jurisdiction_level: JurisdictionLevelSchema.optional(),
    is_active: z.coerce.boolean().optional(),
    effective_date: z.string().date().optional(),
    search: z.string().optional(),
    code: z.string().optional(),
    sort_by: z.enum(['created_at', 'updated_at', 'name', 'code']).optional(),
    sort_order: z.enum(['asc', 'desc']).optional(),
});
export const TaxResponseSchema = TaxSchema;
export const PaginatedTaxesResponseSchema = z.object({
    data: z.array(TaxResponseSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        total_pages: z.number(),
        has_next: z.boolean(),
        has_prev: z.boolean(),
    }),
});
export const TaxParamsSchema = z.object({
    id: z.string(),
});
export const CalculateTaxRequestSchema = z.object({
    amount: z.number().positive(),
    tax_ids: z.array(z.string()).optional(),
});
export const CalculateTaxResponseSchema = z.object({
    subtotal: z.number(),
    tax_amount: z.number(),
    total: z.number(),
    tax_details: z.array(z.object({
        tax_id: z.string(),
        tax_name: z.string(),
        tax_amount: z.number(),
    })),
});
export const TAX_TYPE = TaxTypeSchema.enum;
export const TAX_TYPES = TaxTypeSchema.options;
export const APPLIES_TO = AppliesToSchema.enum;
export const APPLIES_TO_OPTIONS = AppliesToSchema.options;
export const TAXABLE_AMOUNT_TYPE = TaxableAmountTypeSchema.enum;
export const TAXABLE_AMOUNT_TYPES = TaxableAmountTypeSchema.options;
export const AssignedTaxSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    tax_type: TaxTypeSchema,
    percentage_value: z.number().nullable().optional(),
    fixed_amount_cents: z.number().nullable().optional(),
    priority: z.number(),
    is_active: z.boolean(),
    assigned_at: z.string().datetime(),
});
export const OrganizationTaxSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    tax_type: TaxTypeSchema,
    percentage_value: z.number().nullable(),
    fixed_amount_cents: z.number().nullable(),
    organization_name: z.string().optional(),
    source: z.literal('organization').optional(),
});
export const EffectiveTaxSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    tax_type: TaxTypeSchema,
    percentage_value: z.number().nullable().optional(),
    fixed_amount_cents: z.number().nullable().optional(),
    priority: z.number().optional(),
    is_active: z.boolean().optional(),
    assigned_at: z.string().datetime().optional(),
    source: z.enum(['customer', 'organization']).optional(),
    organization_name: z.string().optional(),
});
export const AssignTaxesSchema = z.object({
    tax_ids: z.array(z.string()).min(1, 'At least one tax ID is required'),
    replace_existing: z.boolean().optional(),
});
export const CustomerTaxesResponseSchema = z.object({
    customer_id: z.string(),
    customer_name: z.string(),
    tax_exempt: z.boolean(),
    tax_exemption_certificate: z.string().nullable(),
    tax_exemption_reason: z.string().nullable(),
    customer_taxes: z.array(AssignedTaxSchema),
    organization_taxes: z.array(OrganizationTaxSchema).nullable(),
    effective_taxes: z.array(EffectiveTaxSchema),
});
export const RemoveTaxResponseSchema = z.object({
    customer_id: z.string(),
    tax_id: z.string(),
    message: z.string(),
    note: z.string().optional(),
});
export const SetTaxExemptionSchema = z.object({
    tax_exempt: z.boolean(),
    tax_exemption_certificate: z.string().optional(),
    tax_exemption_reason: z.string().optional(),
});
export const TaxExemptionResponseSchema = z.object({
    customer_id: z.string(),
    tax_exempt: z.boolean(),
    tax_exemption_certificate: z.string().nullable(),
    tax_exemption_reason: z.string().nullable(),
    updated_at: z.string().datetime(),
    message: z.string(),
});
export const OrganizationTaxesResponseSchema = z.object({
    organization_id: z.string(),
    organization_name: z.string(),
    taxes: z.array(AssignedTaxSchema),
    total: z.number().optional(),
});
export const RemoveOrganizationTaxResponseSchema = z.object({
    organization_id: z.string(),
    tax_id: z.string(),
    message: z.string(),
});
//# sourceMappingURL=index.js.map